const SEED_KEY = "dm_tarot_seed_v1";

export function getLocalDateKey(d = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getOrCreateUserSeed(): string {
  try {
    const existing = localStorage.getItem(SEED_KEY);
    if (existing) return existing;

    const seed = generateSeed();
    localStorage.setItem(SEED_KEY, seed);
    return seed;
  } catch {
    // If storage is blocked, still return a session seed.
    return generateSeed();
  }
}

function generateSeed(): string {
  // Prefer crypto for stable randomness.
  try {
    const arr = new Uint32Array(4);
    crypto.getRandomValues(arr);
    return Array.from(arr).map(n => n.toString(16)).join("");
  } catch {
    // Fallback
    return `${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
  }
}

function hashStringToInt(input: string): number {
  // Simple deterministic hash (fast, good enough for 22 items).
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // 32-bit
  }
  return Math.abs(hash);
}

export function getTodayCardIndex(totalCards: number): number {
  const seed = getOrCreateUserSeed();
  const dateKey = getLocalDateKey();
  const value = hashStringToInt(`${dateKey}:${seed}`);
  return totalCards > 0 ? value % totalCards : 0;
}

// --- History & Streak Logic ---

const STREAK_KEY = "dm_tarot_streak_v1";
const LAST_VISIT_KEY = "dm_tarot_last_visit_v1";

export function getUserStreak(): number {
  try {
    return parseInt(localStorage.getItem(STREAK_KEY) || "0", 10);
  } catch {
    return 0;
  }
}

export function checkAndIncrementStreak(todayKey: string): number {
  try {
    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    let streak = getUserStreak();

    // If already visited today, just return current streak
    if (lastVisit === todayKey) {
      return streak;
    }

    // Check if last visit was yesterday
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yesterdayKey = getLocalDateKey(d);

    if (lastVisit === yesterdayKey) {
      streak += 1;
    } else {
      // Reset if missed a day (or first time), but today counts as 1
      streak = 1;
    }

    localStorage.setItem(STREAK_KEY, streak.toString());
    localStorage.setItem(LAST_VISIT_KEY, todayKey);
    
    return streak;
  } catch {
    return 0;
  }
}

export function getDailySpread(totalCards: number): number[] {
  const seed = getOrCreateUserSeed();
  const dateKey = getLocalDateKey();
  
  // We need 3 unique indices
  const indices: number[] = [];
  let attempt = 0;
  
  while (indices.length < 3 && attempt < 100) {
    const value = hashStringToInt(`${dateKey}:${seed}:spread:${attempt}`);
    const index = value % totalCards;
    if (!indices.includes(index)) {
      indices.push(index);
    }
    attempt++;
  }
  
  return indices;
}

// --- Journal Logic ---

const JOURNAL_KEY = "dm_tarot_journal_v1";

export function getJournalEntry(cardId: string, dateKey: string): string {
  try {
    const store = JSON.parse(localStorage.getItem(JOURNAL_KEY) || "{}");
    return store[`${dateKey}:${cardId}`] || "";
  } catch {
    return "";
  }
}

export function saveJournalEntry(cardId: string, dateKey: string, text: string) {
  try {
    const store = JSON.parse(localStorage.getItem(JOURNAL_KEY) || "{}");
    store[`${dateKey}:${cardId}`] = text;
    localStorage.setItem(JOURNAL_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}
